import { useState, useEffect, useRef, useCallback } from 'react';
import styled from 'styled-components';
import { map, values } from 'lodash';
import PropTypes from 'prop-types';

import Text from '../typography/Text';
import Icon, { Caret } from '../Icon';

import { FlexRow } from '../Flex';

import { COLORS, MobileSize } from '../../helpers/constants/styleguide';

const height = 48;
const width = 224;
const yPadding = 12;
const xPadding = 16;

export const SelectOption = ({
  label,
  handleClick,
  textColor = 'default',
  icon,
  iconColor = COLORS.darkerGrey,
  disabled = false,
}) => {
  const handleKeyDown = event => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      if (!disabled && handleClick) {
        handleClick();
      }
    }
  };

  const handleClickWrapper = () => {
    if (!disabled && handleClick) {
      handleClick();
    }
  };

  return (
    <OptionContainer
      tabIndex={disabled ? -1 : 0}
      onClick={handleClickWrapper}
      onKeyDown={handleKeyDown}
      iconColor={iconColor}
      disabled={disabled}
      role="menuitem"
      aria-disabled={disabled}
    >
      {icon && (
        <Icon
          SvgComponent={icon}
          fill={disabled ? COLORS.medGrey : iconColor}
          width={22}
          height={22}
          style={{
            marginRight: '8px',
            cursor: disabled ? 'not-allowed' : 'pointer',
          }}
        />
      )}
      <Text as="label" bold color={disabled ? 'secondary' : textColor}>
        {label}
      </Text>
    </OptionContainer>
  );
};

/**
 * @typedef {Object} SelectButtonOption
 * @property {string} label
 * @property {() => void} [handleClick]
 * @property {() => void} [onClick]
 * @property {'default' | 'secondary' | 'summer' | 'error' | 'light' | 'dark'} [textColor]
 * @property {object} [icon]
 * @property {string} [iconColor]
 * @property {boolean} [disabled]
 * @property {string} [key]
 */

/**
 * @typedef {Object} SelectButtonProps
 * @property {string} label
 * @property {SelectButtonOption[]} selectOptions
 * @property {boolean} [disabled]
 * @property {string} [className]
 * @property {string} [ariaLabel]
 * @property {string} [disabledTitle]
 */

/** @type {React.FC<SelectButtonProps>} */
const SelectButton = ({
  label,
  selectOptions,
  disabled = false,
  className,
  ariaLabel,
  disabledTitle,
}) => {
  const ref = useRef();
  const [isOpen, setIsOpen] = useState(false);

  const toggleDropdown = useCallback(() => {
    if (!disabled) {
      setIsOpen(!isOpen);
    }
  }, [isOpen, disabled]);

  useEffect(() => {
    const handleClickOutside = e => {
      if (ref.current && ref.current.contains(e.target)) {
        return;
      }
      if (isOpen) {
        setIsOpen(false);
      }
    };

    const handleEscapeKey = e => {
      if (e.key === 'Escape' && isOpen) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscapeKey);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [isOpen]);

  const handleKeyDown = event => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      toggleDropdown();
    }
  };

  return (
    <Container ref={ref} className={className}>
      <ButtonContainer
        className={`${isOpen ? 'open' : ''} ${disabled ? 'disabled' : ''}`}
        disabled={disabled}
      >
        <InnerContainer>
          <Text as="label" bold left color={disabled ? 'secondary' : 'default'}>
            {label}
          </Text>
          <Arrow
            SvgComponent={Caret}
            fill={disabled ? COLORS.medGrey : COLORS.darkerGrey}
            disabled={disabled}
          />
        </InnerContainer>
      </ButtonContainer>
      <ShadowContainer
        onClick={toggleDropdown}
        onKeyDown={handleKeyDown}
        className={`${isOpen ? 'open' : ''} ${disabled ? 'disabled' : ''}`}
        tabIndex={disabled ? -1 : 0}
        disabled={disabled ? true : undefined}
        role="button"
        aria-haspopup="true"
        aria-expanded={isOpen}
        aria-label={ariaLabel || `${label} menu`}
        title={disabled ? disabledTitle : label}
      />
      {isOpen && !disabled && (
        <DropdownContainer
          role="menu"
          aria-label={ariaLabel || `${label} options`}
        >
          {map(
            selectOptions,
            ({
              label: optionLabel,
              handleClick,
              onClick,
              textColor,
              icon,
              iconColor,
              disabled: optionDisabled,
              key,
            }) => {
              const clickHandler = handleClick || onClick;
              return (
                <SelectOption
                  key={key || optionLabel}
                  label={optionLabel}
                  textColor={textColor}
                  icon={icon}
                  iconColor={iconColor}
                  disabled={optionDisabled}
                  handleClick={() => {
                    if (!optionDisabled && clickHandler) {
                      setIsOpen(false);
                      clickHandler();
                    }
                  }}
                />
              );
            },
          )}
        </DropdownContainer>
      )}
    </Container>
  );
};

SelectOption.propTypes = {
  textColor: PropTypes.oneOf([
    'default',
    'secondary',
    'summer',
    'error',
    'light',
    'dark',
  ]),
  label: PropTypes.string.isRequired,
  handleClick: PropTypes.func,
  icon: PropTypes.object,
  iconColor: PropTypes.oneOf(values(COLORS)),
  disabled: PropTypes.bool,
};

SelectButton.propTypes = {
  label: PropTypes.string.isRequired,
  selectOptions: PropTypes.arrayOf(PropTypes.shape(SelectOption.propTypes))
    .isRequired,
  disabled: PropTypes.bool,
  className: PropTypes.string,
  ariaLabel: PropTypes.string,
  disabledTitle: PropTypes.string,
};

const boxShadow = '0 0 10px 0 rgba(65, 74, 88, 0.32)';

const Container = styled.div`
  position: relative;
  max-width: ${width}px;
  width: 100%;
  height: ${height}px;
  @media (max-width: ${MobileSize}) {
    width: 100%;
  }
`;

/* ButtonContainer and ShadowContainer are two separate components because we need to have box
 *  shadows on both the button and the dropdown without the shadow from one appearing over the
 *  other.
 * ShadowContainer handles the shadow and click functionality and ButtonContainer handles the text
 *  and background.
 * ButtonContainer has a higher z-index than the dropdown and sits above its shadow.
 */
const ShadowContainer = styled.div`
  max-width: ${width}px;
  width: 100%;
  height: ${height}px;
  position: absolute;
  border-radius: 3px;
  top: 0;
  left: 0;
  cursor: ${props => (props.disabled ? 'not-allowed' : 'pointer')};

  &:hover:not(.disabled),
  &.open:not(.disabled) {
    box-shadow: ${boxShadow};
  }

  &:focus-visible:not(.disabled) {
    outline: none;
    box-shadow:
      0 0 0 2.5px ${COLORS.white},
      0px 0px 0px 4px ${COLORS.azure};
  }

  &.disabled {
    cursor: not-allowed;
  }
`;

const ButtonContainer = styled.div`
  max-width: ${width}px;
  width: 100%;
  height: ${height}px;
  position: absolute;
  top: 0;
  left: 0;
  padding: ${yPadding}px ${xPadding}px;
  border-radius: 3px;
  border: solid 1px ${COLORS.grey};
  background-color: ${COLORS.white};
  pointer-events: none;

  &.open:not(.disabled) {
    border-radius: 3px 3px 0 0;
    z-index: 2;
  }

  &.disabled {
    background-color: ${COLORS.lightGrey};
    border-color: ${COLORS.lighterGrey};
  }
`;

const InnerContainer = styled(FlexRow)`
  align-items: center;
  justify-content: space-between;
  & label {
    user-select: none;
    cursor: pointer;
  }
  @media (max-width: ${MobileSize}) {
    justify-content: center;
  }
`;

const Arrow = styled(Icon)`
  cursor: pointer;
  padding-left: 12px;
`;

const DropdownContainer = styled.div`
  position: absolute;
  top: ${height}px;
  left: 0;
  z-index: 2;
  max-width: ${width}px;
  width: 100%;

  border-radius: 0 0 3px 3px;
  border-left: solid 1px ${COLORS.grey};
  border-right: solid 1px ${COLORS.grey};
  border-bottom: solid 1px ${COLORS.grey};
  box-shadow: ${boxShadow};
  background-color: ${COLORS.white};
`;

const OptionContainer = styled(FlexRow)`
  padding: ${yPadding}px ${xPadding}px;
  cursor: ${props => (props.disabled ? 'not-allowed' : 'pointer')};
  background-color: ${COLORS.white};
  align-items: center;

  &:focus {
    outline: none;
  }

  &:hover:not([disabled]),
  &:focus-visible:not([disabled]) {
    background-color: ${COLORS.darkerGrey};
    outline: none;
    label {
      color: ${COLORS.white};
    }
    path,
    g {
      fill: ${COLORS.white} !important;
    }
  }

  &[disabled] {
    opacity: 0.6;
  }

  & label {
    user-select: none;
    cursor: ${props => (props.disabled ? 'not-allowed' : 'pointer')};
  }

  &:last-child {
    border-radius: 0 0 3px 3px;
  }

  @media (max-width: ${MobileSize}) {
    justify-content: center;
  }
`;

export default SelectButton;
